import type { FC } from 'react'
import { memo } from 'react'

import { Flex, Space, SubText, Text } from '@procurify/ui'
import * as Sentry from '@sentry/react'
import { Severity } from '@sentry/types'
import { useIntl } from 'react-intl'

import { toast } from '@webapp/components/Notification'
import { UserAvatar } from '@webapp/components/UserAvatar'
import { FormattedRelativeTime } from '@webapp/core/components/FormattedTime'
import { decodeHtmlEntities } from '@webapp/utils'
import { getFullName } from '@webapp/utils/user'
import messages from './messages'
import { type LegacyComment } from '../../Chat'
import { ChatCommentStyled } from './styles/ChatCommentStyled'

export interface IChatCommentProps {
  isReceiver?: boolean
  comment: LegacyComment
}

export const ChatComment: FC<IChatCommentProps> = memo(
  (props: IChatCommentProps) => {
    const { comment, isReceiver } = props

    const intl = useIntl()

    const flexDirection = isReceiver ? 'row-reverse' : 'row'

    const renderCommentHTML = (unformattedComment: string) => {
      const matchedTags = unformattedComment.match(/<[^<>]*>([^<]*)<\/[^<>]*>/g)

      if (!matchedTags) return decodeHtmlEntities(unformattedComment)

      const escapeTagForRegex = (tag: string) =>
        tag.replace(/[.+?^${}()|[\]\\]/g, String.raw`\$&`)

      try {
        if (matchedTags.find((tag) => tag.includes('*')))
          throw new Error('Username contains invalid character')

        const regexSplit = new RegExp(
          matchedTags.map((tag) => `(${escapeTagForRegex(tag)})`).join('|')
        )

        const formattedComment = unformattedComment
          .split(regexSplit)
          .map((substr, index) => {
            if (!substr) return ''

            const substrMatch = /<[^<>]*>([^<]*)<\/[^<>]*>/.exec(substr)
            if (!substrMatch?.[1]) return decodeHtmlEntities(substr)

            return (
              <span key={index} className='mention'>
                {decodeHtmlEntities(substrMatch[1])}
              </span>
            )
          })

        return formattedComment
      } catch (e) {
        toast.error({
          children: intl.formatMessage(messages.usernameParseError),
          title: 'Error',
        })

        Sentry.withScope((scope) => {
          scope.setLevel(Severity.Warning)
          Sentry.captureException({
            message: 'ChatComment: failed to format comment',
            originalException: e,
          })
        })
      }
    }

    return (
      <ChatCommentStyled isFromMe={isReceiver}>
        <Flex.Row flexWrap='nowrap' flexDirection={flexDirection} spacing={0.5}>
          <Flex.Item>
            <UserAvatar
              size='medium'
              name={getFullName(comment.userprofile)}
              userId={comment.userprofile.id}
              url={comment.userprofile.profile_image}
            />
          </Flex.Item>
          <Flex.Item flexGrow={1}>
            <div className='comment'>
              <Space size='xsmall'>
                <SubText className='comment-header'>
                  <span className='comment-author' data-testid='author'>
                    {getFullName(comment.userprofile)}
                  </span>{' '}
                  {intl.formatMessage(messages.wroteLabel)}:
                </SubText>
                <SubText
                  autoMargin={false}
                  className='comment-date'
                  data-testid='date'
                >
                  <FormattedRelativeTime
                    date={new Date(comment.submit_date)}
                    autoUpdate
                  />
                </SubText>
                <Text autoMargin={false} data-testid='content'>
                  {renderCommentHTML(comment.comment)}
                </Text>
              </Space>
            </div>
          </Flex.Item>
        </Flex.Row>
      </ChatCommentStyled>
    )
  }
)

ChatComment.defaultProps = {
  isReceiver: false,
}
ChatComment.displayName = 'ChatComment'
